import React, { useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import {
  accessUtil,
  authUtil,
  productCapUtil,
  uxAnalyticsUtil,
} from '../../utils';
import PropTypes from 'prop-types';

import { getCoverImage } from './bookCover/coverWrapper';
import { Button, Segment, Icon } from 'semantic-ui-react';
import { fetchCurrentBook } from 'src/modules/book';
import BetaIcon from 'src/components/BetaIcon';
import { CenteredContent } from 'src/style';
import UpgradeToUnlock from 'src/components/payments/UpgradeToUnlock';
import CopyText from 'src/components/sharing/CopyText';
import useBookPublishabilityReport from 'src/hooks/usePublishabilityReport';
import { shouldEnableRefreshAnalysis } from 'src/utils/analysisVersionUtils';
import FeedbackButton from 'src/components/feedback/FeedbackButton';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;
const TitleContent = styled.h1`
  flex: 1;
  font-size: 1.5em;
  font-weight: bold;
`;
const Right = styled.div`
  margin-left: auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 5px;
`;

const ScoreCircle = styled.div<{ score: number }>`
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: conic-gradient(
    #28a745 0deg ${props => (props.score / 100) * 360}deg,
    #e9ecef ${props => (props.score / 100) * 360}deg 360deg
  );
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  margin: 20px auto;

  &::before {
    content: '';
    position: absolute;
    width: 90px;
    height: 90px;
    background: var(--br-surface);
    border-radius: 50%;
  }
`;

const ScoreText = styled.div`
  position: relative;
  z-index: 1;
  font-size: 24px;
  font-weight: bold;
  color: var(--br-text);
  text-align: center;

  .score-number {
    font-size: 28px;
    display: block;
  }

  .score-label {
    font-size: 12px;
    color: var(--br-muted);
    margin-top: 2px;
  }
`;

const ScoreContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ReasoningSection = styled.div`
  flex: 1;
  min-width: 300px;

  h4 {
    margin-top: 0;
    margin-bottom: 15px;
    color: var(--br-text);
    font-size: 16px;
    font-weight: 600;
  }

  p {
    line-height: 1.6;
    color: var(--br-muted);
    margin: 0;
    text-align: justify;
  }
`;

const MyCenteredContent = styled(CenteredContent)`
  padding-left: 1rem;
  padding-right: 1rem;
`;

const MetaData = styled(({ smallScreen, ...rest }) => <div {...rest} />)`
  background-color: var(--br-surface);
  padding: 3px;
  display: flex !important;
  flex-direction: row;
  justify-content: space-between;
  font-size: 1em !important;
  border-radius: 5px !important;
  color: grey;
`;

MetaData.propTypes = {
  smallScreen: PropTypes.bool,
};

type GenerateSummaryProps = {
  idToken: string;
  bookId: string;
  contentId: string;
};

const BookPublishability = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const currentBook = useSelector(state => state.book.currentBook);
  const userProfile = useSelector(state => state.user.userProfile);
  const publishabilityReport = useBookPublishabilityReport({
    bookId: currentBook?._id,
    contentId: currentBook?.content?.at(-1).id,
  });


  const isFreeUser = productCapUtil.isOnFreePlan({ userProfile });

  const mostRecentAnalysisReport =
    publishabilityReport.mostRecentPublishabilityReport?.data;

  // Move useMemo to top of component before any early returns
  const shouldEnableRefresh = useMemo(() => {
    return shouldEnableRefreshAnalysis(currentBook, mostRecentAnalysisReport);
  }, [currentBook, mostRecentAnalysisReport]);

  console.log(
    'mostRecentAnalysisReport',
    publishabilityReport.mostRecentPublishabilityReport?.data
  );

  const canEdit = useCallback(() => {
    return accessUtil.isAllowed(currentBook?.access, ['edit']);
  }, [currentBook?.access]);

  const isAuthorOrCollaborator = useCallback(
    () => ['author', 'collaborator'].includes(currentBook.role),
    [currentBook?.role]
  );

  const refreshBook = useCallback(async () => {
    const idToken = await authUtil.getFreshIdToken();
    dispatch(
      fetchCurrentBook({ idToken, bookId: currentBook._id, token: undefined })
    );
  }, [currentBook]);

  const handleGenerateSummaryClick = useCallback(async () => {
    uxAnalyticsUtil.trackEvent({
      category: 'AI',
      action: `clicked-generate-summary`,
    });
    const idToken = await authUtil.getFreshIdToken();
    try {
      await publishabilityReport.generatePublishabilityReport({
        bookId: currentBook.id,
        contentId: currentBook.content.at(-1).id,
      });
      dispatch(
        fetchCurrentBook({ idToken, bookId: currentBook._id, token: undefined })
      );
    } catch (error) {
      console.log(error);
    }
  }, [currentBook]);

  if (!currentBook) {
    return null;
  }

  const cover = getCoverImage(currentBook);

  const hasAnalysis = mostRecentAnalysisReport?.updatedAt !== null;

  if (!canEdit()) {
    return (
      <MyCenteredContent>
        <Segment>{t('ActionCancelled')}</Segment>
      </MyCenteredContent>
    );
  }

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{currentBook.title}</title>
        <meta
          property="og:title"
          content={`${t('Read')} ${currentBook.title} ${t('by')} ${
            currentBook.authorName
          } ${t('onBetaReader')}`}
        />
        <meta property="og:site_name" content="BetaReader.io" />
        <meta property="og:description" content={currentBook.description} />
        <meta property="og:image" content={cover} />
      </Helmet>
      <MyCenteredContent>
        <TitleWrapper>
          <TitleContent>
            {t('Publishability')} (beta)
            <BetaIcon />
            <FeedbackButton
              featureName={t('Publishability')}
              url={window.location.pathname}
              style={{ marginLeft: '8px' }}
            />
          </TitleContent>
          {canEdit() && isAuthorOrCollaborator() && (
            <Right>
              <Button.Group>
                <Button
                  disabled={isFreeUser || !shouldEnableRefresh}
                  loading={publishabilityReport.isLoading}
                  onClick={handleGenerateSummaryClick}
                  icon={hasAnalysis ? 'refresh' : 'plus'}
                  content={
                    hasAnalysis ? t('RefreshAnalysis') : t('StartAnalysis')
                  }
                />
              </Button.Group>
              {isFreeUser && <UpgradeToUnlock />}
            </Right>
          )}
        </TitleWrapper>

        <em>
          {t('ManuscriptAnalysisDescription', { title: currentBook.title })}
        </em>

        {mostRecentAnalysisReport?.results && (
          <>
            {mostRecentAnalysisReport.results.synopsis && (
              <>
                <TitleContent>
                  {t('Synopsis')}{' '}
                  <CopyText
                    text={mostRecentAnalysisReport.results.synopsis}
                    title={t('Synopsis')}
                  />
                </TitleContent>
                <Segment>{mostRecentAnalysisReport.results.synopsis}</Segment>
              </>
            )}

            {mostRecentAnalysisReport.results.genre && (
              <>
                <TitleContent>{t('Genre')}</TitleContent>
                <Segment>{mostRecentAnalysisReport.results.genre}</Segment>
              </>
            )}

            {typeof mostRecentAnalysisReport.results.publishabilityScore ===
              'number' && (
              <>
                <TitleContent>{t('PublishabilityScore')}</TitleContent>
                <Segment>
                  <ScoreContainer>
                    <ScoreCircle
                      score={
                        mostRecentAnalysisReport.results.publishabilityScore
                      }
                    >
                      <ScoreText>
                        <span className="score-number">
                          {mostRecentAnalysisReport.results.publishabilityScore}
                        </span>
                        <span className="score-label">out of 100</span>
                      </ScoreText>
                    </ScoreCircle>
                    {mostRecentAnalysisReport.results
                      .publishabilityReasoning && (
                      <ReasoningSection>
                        <h4>{t('Reasoning')}</h4>
                        <p>
                          {
                            mostRecentAnalysisReport.results
                              .publishabilityReasoning
                          }
                        </p>
                      </ReasoningSection>
                    )}
                  </ScoreContainer>
                </Segment>
              </>
            )}
          </>
        )}

        {(() => {
          const status =
            mostRecentAnalysisReport?.results?.status ||
            mostRecentAnalysisReport?.status;
          const processingStatuses = [
            'uploaded',
            'extracting_metadata',
            'analyzing_content',
            'analyzing_publishability',
          ];

          console.log('status', status);

          if (processingStatuses.includes(status)) {
            return (
              <Segment>
                <div>
                  <Icon name="spinner" loading />
                  {t('AnalysisInProgress')}
                  {mostRecentAnalysisReport?.results?.processingStatus && (
                    <div
                      style={{
                        marginTop: '5px',
                        fontSize: '0.9em',
                        color: '#666',
                      }}
                    >
                      {t(`analysisStatus.${status}`)}
                    </div>
                  )}
                </div>
              </Segment>
            );
          }

          if (status === 'error') {
            return (
              <Segment color="red">
                <div>
                  <Icon name="exclamation triangle" />
                  {t('AnalysisFailed')}
                  {mostRecentAnalysisReport?.results?.processingStatus && (
                    <div style={{ marginTop: '5px', fontSize: '0.9em' }}>
                      {mostRecentAnalysisReport.results.processingStatus}
                    </div>
                  )}
                </div>
              </Segment>
            );
          }

          return null;
        })()}
      </MyCenteredContent>
    </>
  );
};

export default BookPublishability;
